import { type FC, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { formatProviderName } from '../provider-name'

interface AvatarProps {
  /** Provider name — used to deterministically pick the fallback colour. */
  name?: string | null | undefined
  /** Post-nominals ("MD", "DO") appended to the photo's alt text. */
  credentials?: string | null | undefined
  url?: string | null | undefined
  className?: string | undefined
  /**
   * Render nothing instead of the fallback person icon when there is no usable
   * photo. The provider page uses this: a placeholder headshot there is dead
   * space (VIT-18153). Result cards leave it off — the icon anchors the row.
   */
  hideWhenMissing?: boolean | undefined
}

/**
 * Avatar fallback colour palette (solid hex backgrounds for the person-icon
 * SVG). Kept in sync with atlas-mcp's `AVATAR_FALLBACK_COLORS` so the fallback
 * avatar looks consistent across the two apps. Add entries to extend the
 * palette — `getAvatarFallbackColor` adapts via modulo, so no call-site changes
 * are needed.
 */
const AVATAR_FALLBACK_COLORS = ['#4A59FF', '#D54AFF', '#FF624A', '#FF4A68', '#984AFF'] as const

/**
 * Deterministically select an avatar fallback colour from a name. Hashes the
 * name's character codes and maps into the palette via modulo, so the same name
 * always gets the same colour.
 */
const getAvatarFallbackColor = (name: string): string => {
  const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return AVATAR_FALLBACK_COLORS[hash % AVATAR_FALLBACK_COLORS.length] || '#984AFF'
}

/**
 * Whether an `<img>` element has already finished a load that produced no image.
 *
 * The page is server-rendered, so the browser can start — and finish — the photo
 * request before React hydrates. When it fails in that window the `onError` handler
 * is attached too late to ever fire, and the element sits there as the browser's own
 * broken-image glyph instead of our fallback. A `complete` element with a zero
 * `naturalWidth` is exactly that: the request ended, and there are no pixels
 * (VIT-17960).
 */
export const isFailedImage = (img: Pick<HTMLImageElement, 'complete' | 'naturalWidth'> | null): boolean =>
  img?.complete === true && img.naturalWidth === 0

/**
 * Alt text for a headshot: the provider's name, with post-nominals appended when
 * we have them ("Jane Doe, MD") — the same string the heading beside it shows.
 *
 * Literally the same string: both compose `formatProviderName`, so the alt text
 * and the visible heading can't drift into disagreeing about punctuation
 * (VIT-18200).
 *
 * Empty when there is no name: an unnamed face has nothing truthful to say, and
 * an empty alt correctly marks it decorative. No "Photo of" prefix — assistive
 * tech already announces the image role, so it would just be noise.
 */
export const toAvatarAlt = (name: string | null | undefined, credentials: string | null | undefined): string =>
  formatProviderName(name, credentials)

export const Avatar: FC<AvatarProps> = ({ name, credentials, url, className, hideWhenMissing }) => {
  // Track the URL that failed rather than a component-wide boolean, so a new
  // photo URL can still load after a previous one errored.
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  // A blank URL is no URL: the loader can hand back an empty string, and
  // `<img src="">` re-requests the page itself rather than a photo.
  const photoUrl = url?.trim() === '' ? null : url
  const showImage = photoUrl != null && failedUrl !== photoUrl
  const bg = getAvatarFallbackColor(name ?? '')
  // One accessible name for both branches, so a provider without a headshot reads
  // the same as one with a photo.
  const avatarAlt = toAvatarAlt(name, credentials)
  // Only the fallback branch needs the wrapper to carry that name: when the photo
  // renders, its `alt` is the accessible name. Labelling both would announce the
  // provider twice — and `role="img"` on the wrapper would hide the `alt` from
  // assistive tech entirely, which is how the headshots ended up crawling as
  // "missing alt text" (VIT-18074).
  const labelWrapper = avatarAlt !== '' && !showImage

  // Nothing to show and the caller doesn't want the placeholder. Checked after
  // `failedUrl`, so a photo that 403s — before or after hydration — leaves the
  // slot empty rather than swapping in the icon we were asked not to render.
  if (!showImage && hideWhenMissing === true) return null

  return (
    <div
      className={twMerge('relative grid size-14 shrink-0 place-items-center overflow-hidden rounded-full', className)}
      role={labelWrapper ? 'img' : undefined}
      aria-label={labelWrapper ? avatarAlt : undefined}
    >
      {showImage ? (
        <img
          src={photoUrl}
          alt={avatarAlt}
          loading="lazy"
          className="absolute inset-0 size-full object-cover"
          // Drop the photo if it fails to load — the coloured person icon takes
          // its place, or nothing at all under `hideWhenMissing`.
          onError={() => setFailedUrl(photoUrl)}
          // ...and catch the failure `onError` misses: one that already happened
          // before hydration attached the handler.
          ref={(img) => {
            if (isFailedImage(img)) setFailedUrl(photoUrl)
          }}
        />
      ) : (
        <svg
          viewBox="0 0 54 54"
          fill="none"
          className="size-full rounded-full border-2 border-white/40"
          style={{ backgroundColor: bg }}
          aria-hidden="true"
        >
          <g fill="white" opacity="0.4">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M22.7151 30.3335C25.572 30.3342 28.4289 30.3342 31.2857 30.3335C32.2546 30.3333 32.9149 30.3331 33.4914 30.4349C36.2516 30.9215 38.4125 33.0825 38.8992 35.8426C39.0008 36.4191 39.0006 37.0793 39.0005 38.0482C39.0004 38.2594 39.0069 38.4719 38.9701 38.6809C38.8241 39.5089 38.1757 40.1571 37.3477 40.3031C37.1662 40.3351 36.9749 40.3342 36.8797 40.3337C30.2942 40.2983 23.7066 40.2983 17.1211 40.3337C17.026 40.3342 16.8347 40.3351 16.6531 40.3031C15.8251 40.1571 15.1768 39.5089 15.0308 38.6809C14.994 38.4719 15.0005 38.2594 15.0004 38.0482C15.0002 37.0793 15 36.4191 15.1017 35.8426C15.5884 33.0825 17.7494 30.9215 20.5094 30.4349C21.086 30.3331 21.7462 30.3333 22.7151 30.3335Z"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M19.667 20.9998C19.667 16.9497 22.9502 13.6665 27.0004 13.6665C31.0504 13.6665 34.3337 16.9497 34.3337 20.9998C34.3337 25.05 31.0504 28.3332 27.0004 28.3332C22.9502 28.3332 19.667 25.05 19.667 20.9998Z"
            />
          </g>
        </svg>
      )}
    </div>
  )
}
